import {Button, DatePicker, Input, Segmented, Select, Switch} from "antd";
import {Link} from "react-router-dom";
import React, {useState} from "react";
import {APIv1} from "../../../api";
import {withPrefilledOption} from "../../../utils/device/deviceUtils";
import dayjs from "dayjs";
import DeviceTerminalFieldsComponent from "./DeviceTerminalFieldsComponent";
import {Building2, CalendarDays, Fingerprint, Plus, UserRound, MapPinHouse} from "lucide-react";

const {RangePicker} = DatePicker
const {TextArea} = Input

const onOk = value => {
    console.log('onOk: ', value)
}

const versionOptions = [
    {label: 'v1 / POS', value: 'v1'},
    {label: 'v2 / SCO', value: 'v2'},
];

const Field = ({label, required, children, wide, icon}) => (
    <div className={`create-device__field ${wide ? "create-device__field--wide" : ""}`}>
        <div className="create-device__field-label">
            {icon && <span className="create-device__field-icon">{icon}</span>}
            <span className={required ? "create-device__required-label" : ""}>{label}</span>
        </div>
        <div className="create-device__field-control">
            {children}
        </div>
    </div>
);

const MainFieldsComponent = ({
    t,
    data,
    onChange,
    userData,
    onChangeDatePicker,
    disabled = false,
    canEditField = () => true,
}) => {
    const [deviceVersions, setDeviceVersions] = useState([]);
    const [deviceUsers, setDeviceUsers] = useState([]);
    const [deviceCompanies, setDeviceCompanies] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchVersions = async () => {
        setLoading(true);
        try {
            const versionsResponse = await APIv1.get('/version/list/', {
                headers: {
                    Authorization: `Token ${userData.token}`
                },
            });
            setDeviceVersions(versionsResponse.data.map(item => ({
                value: item.id,
                label: `${item.version_number} - ${item.status}`,
            })))
        } catch (err) {
            console.error('Something went wrong with versions:', err);
        } finally {
            setLoading(false);
        }
    }
    const fetchUser = async () => {
        setLoading(true);
        try {
            const usersResponse = await APIv1.get('/user/id/list/', {
                headers: {
                    Authorization: `Token ${userData.token}`
                },
            });
            setDeviceUsers(usersResponse.data.map(item => ({
                value: item.id,
                label: item.username,
            })))
        } catch (err) {
            console.error('Something went wrong with users:', err);
        } finally {
            setLoading(false);
        }
    }

    const fetchCompany = async () => {
        try {
            setLoading(true);
            const companiesResponse = await APIv1.get("/company/name/", {
                headers: {
                    Authorization: `Token ${userData.token}`,
                },
            });
            setDeviceCompanies(
                companiesResponse.data.map((item) => ({
                    value: item.id,
                    label: item.name,
                }))
            );
        } catch (err) {
            console.error("Something went wrong with companies:", err);
        } finally {
            setLoading(false);
        }
    };

    const status = data.status ? String(data.status).toUpperCase() : "ACTIVE";
    const isDisabled = field => disabled || !canEditField(field);
    const areDatesDisabled = isDisabled('start_date') || isDisabled('end_date');

    return (
        <div className="create-device__field-grid">
            <Field
                label={t("pages.devices.addNewDeviceColumns.row1")}
                icon={<CalendarDays size={16}/>}
                wide
            >
                <RangePicker
                    className="create-device__control"
                    format='YYYY-MM-DD'
                    onChange={onChangeDatePicker}
                    onOk={onOk}
                    allowClear={true}
                    disabled={areDatesDisabled}
                    value={[
                        data.start_date ? dayjs(data.start_date, 'YYYY-MM-DD HH:mm') : null,
                        data.end_date ? dayjs(data.end_date, 'YYYY-MM-DD HH:mm') : null
                    ]}
                    placeholder={[t("pages.devices.addNewDeviceColumns.placeholders.row1"), t("pages.devices.addNewDeviceColumns.placeholders.row2")]}
                />
            </Field>

            <Field label={t("pages.devices.addNewDeviceColumns.row6")} required icon={<Fingerprint size={16}/>}>
                <Input
                    className="create-device__control"
                    placeholder={t("pages.devices.addNewDeviceColumns.placeholders.row6")}
                    allowClear
                    disabled={isDisabled('device_serial_number')}
                    value={data.device_serial_number || ""}
                    onChange={(e) => onChange({device_serial_number: e.target.value})}
                />
            </Field>

            <Field label={t("pages.devices.addNewDeviceColumns.row2")} icon={<MapPinHouse size={16}/>}>
                <Input
                    className="create-device__control"
                    placeholder={t("pages.devices.addNewDeviceColumns.placeholders.row12")}
                    allowClear
                    disabled={isDisabled('address')}
                    value={data.address || ""}
                    onChange={(e) => onChange({address: e.target.value})}
                />
            </Field>

            <Field label={t("pages.devices.addNewDeviceColumns.row3")} required icon={<UserRound size={16}/>}>
                <Select
                    className="create-device__control"
                    placeholder={t("pages.devices.addNewDeviceColumns.placeholders.row3")}
                    value={data.user_id ?? undefined}
                    disabled={isDisabled('user_id')}
                    onChange={(value) => onChange({user_id: value})}
                    options={withPrefilledOption(deviceUsers, data.user_id, data.user_username)}
                    optionLabelProp="label"
                    showSearch
                    virtual={false}
                    dropdownMatchSelectWidth={false}
                    filterOption={(input, option) =>
                        option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    loading={loading}
                    onDropdownVisibleChange={(open) => {
                        if (open && deviceUsers.length === 0) {
                            fetchUser();
                        }
                    }}
                />
            </Field>

            <Field label={t("pages.devices.addNewDeviceColumns.row4")} required icon={<Building2 size={16}/>}>
                <div className="create-device__inline-control">
                    <Select
                        className="create-device__control"
                        placeholder={t("pages.devices.addNewDeviceColumns.placeholders.row4")}
                        value={data.company_id ?? undefined}
                        disabled={isDisabled('company_id')}
                        onChange={(value) => onChange({company_id: value})}
                        options={withPrefilledOption(deviceCompanies, data.company_id, data.company_name)}
                        optionLabelProp="label"
                        showSearch
                        virtual={false}
                        dropdownMatchSelectWidth={false}
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase())
                        }
                        loading={loading}
                        onDropdownVisibleChange={(open) => {
                            if (open && deviceCompanies.length === 0) {
                                fetchCompany();
                            }
                        }}
                    />
                    {isDisabled('company_id') ? (
                        <Button className="create-device__icon-button" icon={<Plus size={16}/>} disabled/>
                    ) : (
                        <Link to={'/create_company/'} target={'_blank'}>
                            <Button className="create-device__icon-button" icon={<Plus size={16}/>}/>
                        </Link>
                    )}
                </div>
            </Field>

            <Field label={t("pages.devices.addNewDeviceColumns.row5")}>
                <Input
                    className="create-device__control"
                    placeholder={t("pages.devices.addNewDeviceColumns.placeholders.row5")}
                    allowClear
                    disabled={isDisabled('fiscal_token')}
                    value={data.fiscal_token || ""}
                    onChange={(e) => onChange({fiscal_token: e.target.value})}
                />
            </Field>

            <Field label="Fiscal Module">
                <Input
                    className="create-device__control"
                    placeholder="Fiscal Module"
                    allowClear
                    disabled={isDisabled('terminal_id')}
                    value={data.terminal_id ?? ""}
                    onChange={(e) => onChange({terminal_id: e.target.value})}
                />
            </Field>

            <Field label={t("pages.devices.addNewDeviceColumns.row7")} required>
                <Segmented
                    className="create-device__segmented"
                    block
                    disabled={isDisabled('status')}
                    value={status}
                    onChange={(value) => onChange({status: value})}
                    options={[
                        {label: t("common.active"), value: "ACTIVE"},
                        {label: t("common.disconnected"), value: "DISCONNECTED"},
                        {label: t("common.suspended"), value: "INACTIVE"},
                    ]}
                />
            </Field>

            <Field label={t("pages.devices.addNewDeviceColumns.row13")} wide>
                <TextArea
                    className="create-device__control create-device__textarea"
                    placeholder={t("pages.devices.addNewDeviceColumns.placeholders.row15")}
                    allowClear
                    disabled={isDisabled('disconnect_reason')}
                    rows={4}
                    value={data.disconnect_reason || ""}
                    onChange={(e) => onChange({disconnect_reason: e.target.value})}
                />
            </Field>

            <Field label={t("pages.devices.addNewDeviceColumns.row10")} required>
                <Select
                    className="create-device__control"
                    placeholder={t("pages.devices.addNewDeviceColumns.placeholders.row10")}
                    value={data.version_id ?? undefined}
                    disabled={isDisabled('version_id')}
                    onChange={(value, option) => onChange({
                        version_id: value,
                        version_label: option?.label,
                    })}
                    options={withPrefilledOption(deviceVersions, data.version_id, data.version_label)}
                    optionLabelProp="label"
                    showSearch
                    virtual={false}
                    dropdownMatchSelectWidth={false}
                    filterOption={(input, option) =>
                        option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    loading={loading}
                    onDropdownVisibleChange={(open) => {
                        if (open && deviceVersions.length === 0) {
                            fetchVersions();
                        }
                    }}
                />
            </Field>

            <Field label={t("pages.devices.addNewDeviceColumns.row11")} required>
                <Segmented
                    className="create-device__segmented create-device__segmented--compact"
                    options={versionOptions}
                    disabled={isDisabled('fbox_version')}
                    value={data.fbox_version || "v1"}
                    onChange={(value) => onChange({fbox_version: value})}
                />
            </Field>

            <Field label={t("pages.devices.addNewDeviceColumns.row8")}>
                <Switch
                    checked={data.is_multi_user || false}
                    disabled={isDisabled('is_multi_user')}
                    onChange={(checked) => onChange({is_multi_user: checked})}
                />
            </Field>

            <Field label={t("pages.devices.addNewDeviceColumns.row9")}>
                <Switch
                    checked={data.update_available || false}
                    disabled={isDisabled('update_available')}
                    onChange={(checked) => onChange({update_available: checked})}
                />
            </Field>

            <Field label={t("pages.devices.addNewDeviceColumns.row12")} wide>
                <DeviceTerminalFieldsComponent
                    t={t}
                    data={data.device_terminal || {}}
                    onChange={(values) => {
                        const updated = {
                            device_terminal: {
                                ...data.device_terminal,
                                ...values
                            }
                        };
                        onChange(updated);
                    }}
                    userData={userData}
                    disabled={isDisabled('device_terminal')}
                />
            </Field>
        </div>
    )
}

export default MainFieldsComponent;
